/**
 * Settings → Billing agent: labels and formatting for the API's figures
 * (api#908 settings, api#916 metrics and shadow report).
 *
 * NOTHING HERE COMPUTES A METRIC. The API owns every figure, numerator and
 * denominator; this module only words them. The one division (the accepted
 * percentage) is the API's own `accepted / denominator`, shown beside both
 * numbers it came from, never alone.
 */
import { fmtSignedCurrency } from './display-labels';
import { todayCalendarDate } from './calendar-date';
import type {
  BillingAcceptedFigure,
  BillingDollarsCorrected,
  BillingFlagType,
  BillingOfficeHours,
  BillingPreflightMode,
  BillingRulePrecision,
  BillingShadowOutcome,
} from '../types/billing-flags';

/**
 * The flag types, as the office reads them. The same words as the API's
 * FLAG_TYPE_LABEL (`lib/billing-preflight/labels.ts`), which writes the
 * labelling sheet, so the office meets one name per check.
 */
export const BILLING_FLAG_TYPE_LABEL: Record<BillingFlagType, string> = {
  depth_tier: 'Depth tier',
  included_time: 'Overtime past included hours',
  days_on_site: 'Days on site',
  rate_vs_book: 'Rate vs rate sheet',
  unlinked_line: 'Line not linked to rate sheet',
};

/** A flag type's label, or `—` for a type this build does not know. Never the raw key. */
export function billingFlagTypeLabel(type: string | null | undefined): string {
  return type && Object.prototype.hasOwnProperty.call(BILLING_FLAG_TYPE_LABEL, type)
    ? BILLING_FLAG_TYPE_LABEL[type as BillingFlagType]
    : '—';
}

export const isBillingFlagType = (v: unknown): v is BillingFlagType =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(BILLING_FLAG_TYPE_LABEL, v);

export const BILLING_MODE_COPY: Record<BillingPreflightMode, { label: string; sentence: string }> = {
  off: { label: 'Off', sentence: "The agent doesn't check tickets." },
  shadow: {
    label: 'Shadow',
    sentence:
      'The agent checks every field-approved ticket and records what it would have flagged. Nobody sees the flags. Used to measure it before switching it on.',
  },
  suggest: {
    label: 'Suggest',
    sentence:
      'Office staff see flags from active checks on each field-approved ticket. Checks still learning stay hidden. It never changes a ticket and never blocks approval.',
  },
};

export const BILLING_MODES: BillingPreflightMode[] = ['off', 'shadow', 'suggest'];

/**
 * A shadow flag's scored outcome. `unconfirmed` means the office did not make
 * the change the flag pointed at — the flag was not acted on, which is not the
 * same as the flag being wrong, and the label must not say it was.
 */
export const SHADOW_OUTCOME_LABEL: Record<BillingShadowOutcome, string> = {
  agreed: 'Agreed',
  unconfirmed: 'Not acted on',
  pending_office: 'Awaiting office',
  ticket_deleted: 'Ticket deleted',
};

export function shadowOutcomeLabel(outcome: string | null | undefined): string {
  return outcome && Object.prototype.hasOwnProperty.call(SHADOW_OUTCOME_LABEL, outcome)
    ? SHADOW_OUTCOME_LABEL[outcome as BillingShadowOutcome]
    : '—';
}

/**
 * The tenant-local current month and the two before it, oldest first, as
 * 'YYYY-MM'. Null while the tenant zone is unknown — the device's month is not
 * the tenant's, and a guessed month asks the API the wrong question.
 */
export function metricMonthWindow(timeZone: string | null | undefined, now: Date = new Date()): string[] | null {
  const today = todayCalendarDate(timeZone, now);
  if (!today) return null;
  const [y, m] = today.split('-').map(Number);
  return [2, 1, 0].map((back) => {
    const idx = y * 12 + (m - 1) - back;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
  });
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' → 'Oct 2026'; anything else → '—'. */
export function monthLabel(month: string | null | undefined): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month ?? '');
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : '—';
}

/** up / down / net, each signed by its own value. Every part is `—` when the figure is absent. */
export function dollarsCorrectedParts(d: BillingDollarsCorrected | null | undefined) {
  return {
    up: fmtSignedCurrency(d?.up),
    down: fmtSignedCurrency(d?.down),
    net: fmtSignedCurrency(d?.net),
  };
}

const checks = (n: number) => (n === 1 ? 'check' : 'checks');

/**
 * "2 checks active, 3 still learning" — from the API's `counts`, never from a
 * tally of `rules`. `—` when the counts did not arrive.
 */
export function checksHeadline(counts: { active: number; learning: number } | null | undefined): string {
  if (!counts || !Number.isFinite(counts.active) || !Number.isFinite(counts.learning)) return '—';
  return `${counts.active} ${checks(counts.active)} active, ${counts.learning} still learning`;
}

/** "2 of 5 checks will show to office staff." for the Suggest confirmation; null without counts. */
export function suggestChecksSentence(counts: { active: number; learning: number } | null | undefined): string | null {
  if (!counts || !Number.isFinite(counts.active) || !Number.isFinite(counts.learning)) return null;
  const total = counts.active + counts.learning;
  return `${counts.active} of ${total} ${checks(total)} will show to office staff.`;
}

/**
 * A rule's precision as "7 of 10 (70%)", numerator and denominator always. The
 * percentage is the API's `value`. `—` when nothing is recorded (null is "not
 * measured", never 0%) or the figure cannot be read.
 */
export function rulePrecisionPhrase(p: BillingRulePrecision | null | undefined): string {
  if (!p || !Number.isFinite(p.numerator) || !Number.isFinite(p.denominator) || p.denominator <= 0
    || !Number.isFinite(p.value)) return '—';
  return `${p.numerator} of ${p.denominator} (${Math.round(p.value * 100)}%)`;
}

/** "7 of 9 (78%)"; `—` with no figure or a zero denominator. Never a bare percentage. */
export function acceptedPhrase(a: BillingAcceptedFigure | null | undefined): string {
  if (!a || !Number.isFinite(a.denominator) || a.denominator <= 0) return '—';
  return `${a.accepted} of ${a.denominator} (${Math.round((a.accepted / a.denominator) * 100)}%)`;
}

const hours = (h: number | null | undefined) =>
  h === null || h === undefined || !Number.isFinite(h) ? '—' : `${h} h`;

/** "median 4.1 h · p90 26 h · n 31"; with n 0 there is no interval to describe. */
export function officeHoursPhrase(o: BillingOfficeHours | null | undefined): string {
  if (!o || !o.n) return '—';
  return `median ${hours(o.medianH)} · p90 ${hours(o.p90H)} · n ${o.n}`;
}

/** "3 not yet pushed, oldest 52 h", or null when there are none. */
export function notYetPushedPhrase(o: BillingOfficeHours | null | undefined): string | null {
  if (!o || !o.notYetPushed) return null;
  return `${o.notYetPushed} not yet pushed, oldest ${hours(o.oldestUnpushedH)}`;
}

/**
 * The office-hours column's header. It follows the SOURCE: the interim figure
 * (field-approved → office-approved) under a QuickBooks header is the same
 * number under the wrong question.
 */
export function officeHoursHeader(source: BillingOfficeHours['source'] | null | undefined): string {
  if (source === 'qbo') return 'Field-approved → QuickBooks';
  if (source === 'office_approval') return 'Field-approved → office-approved';
  return 'Office hours';
}
